import { Button } from '@/components/ui/Button';
import { useModal } from '@/hooks';
import { cn } from '@/lib/utils';
import {
	CircleChevronRight,
	Play,
	Stethoscope,
	Brain,
	Star,
} from 'lucide-react';
import { Avatar } from '@/components/ui';
import { formatMatchRankLabel } from '@/utils/matchHandoff';
import { ProfessionalTagChips } from '@/components/professional/ProfessionalTagChips';
import {
	ProfessionalShowcasePanel,
	type ShowcaseSlot,
} from '@/components/professional/ProfessionalShowcasePanel';

export interface ProfessionalCardProps {
	/** Id do profissional — necessário para o painel "Conheça o profissional" (números e horários). */
	professional_id?: string;
	video_url?: string | null;
	profile_photo_url?: string | null;
	name_prefix?: string | null;
	first_name: string;
	last_name: string;
	crm_or_crp: string;
	service_name?: string;
	brief_description?: string | null;
	onClick: () => void;
	/**
	 * Clique num horário do painel expandido. Quando informado, recebe o slot
	 * para o próximo passo abrir com ele já selecionado; senão cai em onClick.
	 */
	onScheduleSlot?: (slot: ShowcaseSlot) => void;
	isSelected: boolean;
	/** Texto exibido quando o card já está selecionado. */
	selectedButtonText?: string;
	/** data-testid no botão Selecionar (Playwright) */
	selectButtonTestId?: string;
	className?: string;
	/** Posição no match do usuário (1 = melhor). Quando definido, mostra o selo com estrela. */
	matchRank?: number | null;
	/** Tag de destaque (chip colorido). Ver constants/professionalTags. */
	highlight_tag?: Professionals.HighlightTag | null;
	/** Abordagem principal (linha "Abordagem: ..."). */
	main_approach?: Professionals.Approach | null;
	/** Abordagens complementares. */
	secondary_approaches?: Professionals.Approach[] | null;
	/** Temas de atuação (chips, máx. 3 + "+N"). */
	focus_topics?: Professionals.FocusTopic[] | null;
}

/**
 * Card do profissional em formato de linha (foto | informações | ações), como
 * uma lista de resultados. A altura acompanha o conteúdo: sem tags, sem
 * espaço sobrando. No mobile empilha: foto+nome, tags, bio, botões.
 */
const ProfessionalCard = ({
	professional_id,
	video_url,
	profile_photo_url,
	name_prefix,
	first_name,
	last_name,
	crm_or_crp,
	service_name,
	brief_description,
	onClick,
	onScheduleSlot,
	isSelected,
	selectedButtonText = 'Selecionado',
	selectButtonTestId,
	className,
	matchRank,
	highlight_tag,
	main_approach,
	secondary_approaches,
	focus_topics,
}: ProfessionalCardProps) => {
	const { openModal, closeModal } = useModal();

	const normalizedService = service_name?.toLowerCase() || '';
	const isDoctor =
		normalizedService.includes('médico') ||
		normalizedService.includes('psiquiatra');
	const fullName = [name_prefix, first_name, last_name]
		.filter(Boolean)
		.join(' ');
	const documentLabel = isDoctor ? 'CRM' : 'CRP';
	const description = brief_description?.trim();

	const hasTags = Boolean(
		highlight_tag ||
			main_approach ||
			(secondary_approaches && secondary_approaches.length > 0) ||
			(focus_topics && focus_topics.length > 0),
	);

	const handleOpenAbout = () => {
		openModal({
			size: 'wide',
			content: (
				<ProfessionalShowcasePanel
					professional={{
						id: professional_id ?? '',
						first_name,
						last_name,
						name_prefix,
						crm_or_crp,
						profile_photo_url,
						brief_description,
						video_url,
						highlight_tag,
						main_approach,
						secondary_approaches: secondary_approaches ?? undefined,
						focus_topics: focus_topics ?? undefined,
					}}
					serviceName={service_name}
					onClose={closeModal}
					onSchedule={(slot) => {
						closeModal();
						if (slot && onScheduleSlot) onScheduleSlot(slot);
						else onClick();
					}}
				/>
			),
		});
	};

	return (
		<div
			className={cn(
				'relative flex w-full min-w-0 flex-col gap-4 rounded-[2rem] border bg-white p-5 shadow-sm transition-all md:flex-row md:items-center md:gap-6 md:p-6',
				isSelected
					? 'border-stone-900 ring-1 ring-stone-900'
					: 'border-zinc-100 hover:border-zinc-200 hover:shadow-md',
				matchRank && !isSelected
					? 'border-brand-pink/40 ring-1 ring-brand-pink/30'
					: '',
				className,
			)}
		>
			{matchRank ? (
				<span
					className="absolute -top-3 left-5 inline-flex items-center gap-1 rounded-full bg-brand-pink px-3 py-1 text-[10px] font-black uppercase tracking-[0.16em] text-white shadow-md"
					title="Profissional indicado no seu match"
				>
					<Star className="h-3 w-3 fill-current" />
					{formatMatchRankLabel(matchRank)}
				</span>
			) : null}

			{/* Foto + informações */}
			<div className="flex min-w-0 flex-1 items-start gap-4 md:gap-5">
				<Avatar
					img_url={profile_photo_url || ''}
					variant="rounded"
					className="size-20 shrink-0 rounded-2xl md:size-28 md:rounded-[1.25rem] [&>svg]:size-8"
				/>

				<div className="min-w-0 flex-1 space-y-2">
					<div className="space-y-0.5">
						<h3 className="text-base font-bold leading-tight text-zinc-900 md:text-lg">
							{fullName}
						</h3>
						<div className="flex flex-wrap items-center gap-x-3 gap-y-1">
							{service_name ? (
								<span className="text-[11px] font-bold uppercase tracking-[0.18em] text-zinc-400">
									{service_name}
								</span>
							) : null}
							<span className="inline-flex items-center gap-1.5 text-xs font-medium text-zinc-600">
								{isDoctor ? (
									<Stethoscope className="h-3.5 w-3.5 shrink-0 text-zinc-500" />
								) : (
									<Brain className="h-3.5 w-3.5 shrink-0 text-zinc-500" />
								)}
								{documentLabel} {crm_or_crp}
							</span>
						</div>
					</div>

					{hasTags ? (
						<ProfessionalTagChips
							highlight_tag={highlight_tag}
							main_approach={main_approach}
							secondary_approaches={secondary_approaches}
							focus_topics={focus_topics}
						/>
					) : null}

					{/* Bio só no desktop; no mobile fica para o painel expandido */}
					{description ? (
						<p className="line-clamp-2 hidden text-sm leading-relaxed text-zinc-600 md:block">
							{description}
						</p>
					) : null}
				</div>
			</div>

			{/* Ações: "Conheça" em destaque, depois "Selecionar" */}
			<div className="flex shrink-0 flex-col gap-2 md:w-60">
				<button
					type="button"
					onClick={handleOpenAbout}
					className="group inline-flex w-full items-center justify-center gap-2 rounded-full bg-brand-orange px-4 py-2.5 text-white shadow-sm shadow-brand-orange/30 transition-all hover:bg-brand-orange/90 hover:shadow-md"
				>
					<span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-white/20">
						<Play
							className="h-2.5 w-2.5 fill-white"
							strokeWidth={0}
						/>
					</span>
					<span className="whitespace-nowrap text-[11px] font-black uppercase tracking-[0.1em]">
						Conheça o profissional
					</span>
				</button>
				<Button
					onClick={() => onClick()}
					text={isSelected ? selectedButtonText : 'Selecionar'}
					data-testid={selectButtonTestId}
					variant="outline"
					size="medium"
					className={cn(
						'w-full justify-center rounded-full border-2 py-2 font-bold tracking-wide transition-all',
						'border-stone-900 bg-transparent text-stone-900',
						'gap-3 hover:bg-stone-900 hover:text-white',
						isSelected && 'bg-stone-900 text-white',
					)}
					endIcon={<CircleChevronRight className="h-5 w-5" />}
				/>
			</div>
		</div>
	);
};

export { ProfessionalCard };
